import { useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Button, ActionIcon, Group, Text, Switch } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { IconPrinter, IconPlus, IconMinus, IconTypography } from '@tabler/icons-react';
import { useServiceOrder } from '@/hooks/api-hooks';
import { LoaderMainLogo } from '@/components/logo-loader';
import { localTenant } from '@/services/session-service';
import ServiceOrderManifestView from '@/components/service-orders/service-order-manifest-view';


const MANIFEST_OPTIONS_STORAGE_KEY = 'manifest_display_options';

interface ManifestDisplayOptions {
    fontSize: number;
    showPaxList: boolean;
    onlyFirstPax: boolean;
    showPhone: boolean;
}

const DEFAULT_MANIFEST_OPTIONS: ManifestDisplayOptions = {
    fontSize: 100,
    showPaxList: true,
    onlyFirstPax: false,
    showPhone: true,
};

function loadManifestOptions(): ManifestDisplayOptions {
    try {
        const stored = localStorage.getItem(MANIFEST_OPTIONS_STORAGE_KEY);
        if (stored) {
            return { ...DEFAULT_MANIFEST_OPTIONS, ...JSON.parse(stored) };
        }
    } catch {
        // Ignore storage errors
    }
    return DEFAULT_MANIFEST_OPTIONS;
}

export default function ManifestServiceOrderPage() {
    const { id } = useParams();
    const { t } = useTranslation();
    const tenant = localTenant();
    const {
        data,
        isLoading
    } = useServiceOrder(id!);
    const serviceOrder = data?.data;

    const [options, setOptions] = useState<ManifestDisplayOptions>(loadManifestOptions);
    const { fontSize, showPaxList, onlyFirstPax, showPhone } = options;

    useEffect(() => {
        try {
            localStorage.setItem(MANIFEST_OPTIONS_STORAGE_KEY, JSON.stringify(options));
        } catch {
            // Ignore storage errors
        }
    }, [options]);

    const setFontSize = (updater: (prev: number) => number) => {
        setOptions(prev => ({ ...prev, fontSize: updater(prev.fontSize) }));
    };
    const setShowPaxList = (val: boolean) => {
        setOptions(prev => ({ ...prev, showPaxList: val }));
    };
    const setOnlyFirstPax = (val: boolean) => {
        setOptions(prev => ({ ...prev, onlyFirstPax: val }));
    };
    const setShowPhone = (val: boolean) => {
        setOptions(prev => ({ ...prev, showPhone: val }));
    };

    useEffect(() => {
        document.title = `Manifest_${t('service_order')}_#${serviceOrder?.id}`;
    }, [serviceOrder, t]);

    const handlePrint = () => {
        window.print();
    };

    if (isLoading) {
        return (
            <div className="p-2 max-w-[1024px] mx-auto min-h-screen">
                <LoaderMainLogo />
            </div>
        );
    }

    if (!serviceOrder) return null;

    const increaseFont = () => setFontSize(prev => Math.min(prev + 10, 200));
    const decreaseFont = () => setFontSize(prev => Math.max(prev - 10, 50));

    return (
        <div className="p-2 max-w-[1024px] mx-auto min-h-screen print:min-h-0 print:h-auto">
            <style dangerouslySetInnerHTML={{
                __html: `
                @media screen, print {
                    html {
                        font-size: ${fontSize}% !important;
                    }
                }
            `}} />

            <div className="bg-gray-200 rounded-md p-2 mb-4 flex flex-wrap justify-between items-center gap-4 print:hidden">
                <div className="flex flex-wrap items-center gap-4 px-2">
                    <Switch
                        checked={showPaxList}
                        onChange={(e) => setShowPaxList(e.currentTarget.checked)}
                        label={t('show_pax_list')}
                        size="xs"
                        color="dark"
                    />
                    <Switch
                        checked={onlyFirstPax}
                        onChange={(e) => setOnlyFirstPax(e.currentTarget.checked)}
                        disabled={!showPaxList}
                        label={t('only_first_pax')}
                        size="xs"
                        color="dark"
                    />
                    <Switch
                        checked={showPhone}
                        onChange={(e) => setShowPhone(e.currentTarget.checked)}
                        disabled={!showPaxList}
                        label={t('show_pax_phone')}
                        size="xs"
                        color="dark"
                    />
                </div>

                <div className="flex items-center gap-6 ml-auto">
                    <Group gap="xs">
                        <ActionIcon
                            onClick={decreaseFont}
                            variant="subtle"
                            color="black"
                            size="md"
                            disabled={fontSize <= 50}
                            title={t('decrease_font_size')}
                        >
                            <IconMinus size={16} />
                        </ActionIcon>
                        <Group gap={4} align="center">
                            <IconTypography size={16} stroke={1.5} className="text-gray-500" />
                            <Text size="sm" className="font-bold min-w-[3rem] text-center">
                                {fontSize}%
                            </Text>
                        </Group>
                        <ActionIcon
                            onClick={increaseFont}
                            variant="subtle"
                            color="black"
                            size="md"
                            disabled={fontSize >= 200}
                            title={t('increase_font_size')}
                        >
                            <IconPlus size={16} />
                        </ActionIcon>
                    </Group>

                    <div className="flex gap-6">
                        <Button
                            onClick={handlePrint}
                            color='black'
                            leftSection={<IconPrinter size={16} stroke={1.5} />}
                        >
                            {t('print')}
                        </Button>
                    </div>
                </div>
            </div>

            <div className="space-y-4 print:p-0">

                <ServiceOrderManifestView
                    order={serviceOrder}
                    showPaxList={showPaxList}
                    onlyFirstPax={onlyFirstPax}
                    showPhone={showPhone}
                />

                {/* pointelss data down here */}
                <div className='solid-box-border-slim p-0 text-xxs'>
                    <div className="border-b border-gray-200 px-2 py-1">
                        <span>{t('business_name')} : </span>
                        <span className="font-semibold">{tenant?.settings?.store?.display_name || tenant?.name}</span>
                    </div>
                    {(tenant?.settings?.address || tenant?.settings?.city) && (
                        <div className="border-b border-gray-200 px-2 py-1">
                            <span>{t('address')} : </span>
                            <span className="font-semibold">
                                {[tenant?.settings?.address, tenant?.settings?.city].filter(Boolean).join(', ')}
                            </span>
                        </div>
                    )}
                    {tenant?.settings?.phone && (
                        <div className="border-b border-gray-200 px-2 py-1">
                            <span>{t('phone')} : </span>
                            <span className="font-semibold">{tenant.settings.phone}</span>
                        </div>
                    )}
                    {tenant?.settings?.service_order?.extra_data?.filter(attr => attr.key)?.map((attr, idx) => (
                        <div key={idx} className="border-b border-gray-200 px-2 py-1">
                            <span>{t(attr.key)} : </span>
                            <span className="font-semibold">{attr.value}</span>
                        </div>
                    ))}
                </div>

                <div className='solid-box-border-slim h-30 flex items-end justify-between'>
                    <div>
                        <div className="text-xxs">
                            {t('signature_seller')} :
                        </div>
                        <div className='w-40 h-px bg-gray-200'></div>
                    </div>
                    <div>
                        <div className="text-xxs">
                            {t('signature_provider')} :
                        </div>
                        <div className='w-100 h-px bg-gray-200'></div>
                    </div>
                </div>
            </div>
        </div>
    );
}